import React, { useRef } from 'react'
import Dropzone from 'react-dropzone'
import { Image, Transformation } from 'cloudinary-react'
import AvatarEditor from 'react-avatar-editor'
import Slider from 'rc-slider'
import { round } from 'lodash-es'
import classNames from 'classnames'
import { Button, Svg, LinearProgress } from '../../../../../components'
import styles from './LogoEditor.module.scss'

interface LogoEditorProps {
  uploadProgress?: number | null
  uploadedFileCloudinaryUrl?: string
  uploadedFile?: (File & { preview?: string }) | null
  editing?: boolean
  position?: {
    x: number
    y: number
  }
  cropZoom?: number
  cloudinaryUploadPreset?: string
  onPositionChange?: (position: { x: number; y: number }) => void
  onImageDrop?: (files: any) => void
  onEdit?: () => void
  onClear?: () => void
  onSave?: (editor: any, setFieldValue: any) => void
  onZoomChange?: (zoom: any) => void
  setFieldValue?: (field: string, value: any) => void
  error?: string
}

const LogoEditor: React.FC<LogoEditorProps> = ({
  uploadProgress,
  uploadedFileCloudinaryUrl,
  uploadedFile,
  editing,
  position,
  cropZoom,
  cloudinaryUploadPreset,
  onPositionChange = () => {},
  onImageDrop = () => {},
  onEdit = () => {},
  onClear = () => {},
  onSave = () => {},
  onZoomChange = () => {},
  setFieldValue,
  error,
}) => {
  const editorRef = useRef<any>(null)

  const classes = classNames(styles.root, {
    [styles['has-errors']]: !!error,
  })

  return (
    <div className={classes}>
      <div className={styles['image-container']}>
        {uploadProgress ? (
          <div className={styles['progress-wrapper']}>
            <div className={styles['upload-progress']}>
              <LinearProgress
                percent={uploadProgress}
                strokeColor="#000000"
                trailColor="rgba(0, 0, 0, 0.1)"
                strokeWidth={1.2}
              />
            </div>
          </div>
        ) : (
          <React.Fragment>
            {uploadedFileCloudinaryUrl && !editing ? (
              <div className={styles['saved-pic']}>
                <Image cloudName={cloudinaryUploadPreset} publicId={uploadedFileCloudinaryUrl}>
                  <Transformation width="400" height="400" gravity="faces" crop="fill" />
                  <Transformation width="400" height="400" gravity="faces" crop="scale" />
                </Image>
              </div>
            ) : (
              <div className={styles['file-upload']}>
                {uploadedFile ? (
                  <AvatarEditor
                    ref={editorRef}
                    image={uploadedFile.preview}
                    width={400}
                    height={400}
                    border={0}
                    position={position}
                    scale={cropZoom}
                    rotate={0}
                    onPositionChange={onPositionChange}
                  />
                ) : (
                  <Dropzone onDrop={onImageDrop} multiple={false} accept="image/*" style={null} className="drop-zone">
                    <Svg name="image-icon" />
                    <p>
                      <strong>Upload your logo.</strong>
                      <br />
                      Drag and drop a file here
                    </p>
                    <Button>Choose a file</Button>
                  </Dropzone>
                )}
              </div>
            )}
          </React.Fragment>
        )}

        <div className={styles['context-info']}>
          {uploadProgress ? (
            <p className={styles['uploading-text']}>Uploading</p>
          ) : (
            <React.Fragment>
              {uploadedFileCloudinaryUrl && !editing ? (
                <div className={styles.edit}>
                  <Button link onClick={onEdit}>
                    Edit
                  </Button>
                </div>
              ) : (
                <React.Fragment>
                  {uploadedFile ? (
                    <React.Fragment>
                      <Slider value={round((cropZoom ?? 1) * 25, 0)} onChange={onZoomChange} />
                      <div className={styles['crop-actions']}>
                        <Button link onClick={onClear}>
                          Clear
                        </Button>
                        <Button link onClick={() => onSave(editorRef.current, setFieldValue)}>
                          Save
                        </Button>
                      </div>
                    </React.Fragment>
                  ) : (
                    <p>
                      Your image should be a minimum of 400 x 400 px. For best results trying using a similar image to a
                      Facebook profile picture.
                    </p>
                  )}
                </React.Fragment>
              )}
            </React.Fragment>
          )}
        </div>
      </div>
    </div>
  )
}

export default LogoEditor
